import type { ChauffeurPage } from "../slug-chauffeur";
import { vehiclePriceFaq, vehicleSeo } from "../slugs/vehiclePricing";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";

const suvWithChauffeur: ChauffeurPage = {
  slug: "suv-with-chauffeur",
  title: "Range Rover with chauffeur — luxury SUV",
  subtitle:
    "Luxury SUV with private chauffeur: presence, elegance and versatility from the city to the hinterland.",
  keywords: [
    "Range Rover with chauffeur",
    "luxury SUV with chauffeur Cannes",
    "4x4 with chauffeur French Riviera",
    "chauffeur SUV Nice",
  ],
  hero: {
    title: "Range Rover with chauffeur on the French Riviera — luxury SUV",
    subtitle:
      "The elegance of a high-end SUV with private chauffeur: assertive presence, high seating and comfort, from the Croisette to the hinterland resorts.",
    keywords: [
      "Range Rover with chauffeur",
      "luxury SUV",
      "4x4 with chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Range Rover with private chauffeur in Cannes",
    badges: ["Up to 3 passengers", "Presence & elegance", "Versatile"],
  },
  benefits: {
    heading: "Why the luxury SUV with chauffeur from KS Rent Services",
    items: [
      {
        title: "Presence & elegance",
        description:
          "The Range Rover commands a refined presence: the noticed arrival of a prestige SUV, without ostentation.",
      },
      {
        title: "High seating & comfort",
        description:
          "A commanding seating position, clear views and great suspension comfort: travelling up high, in full serenity.",
      },
      {
        title: "Total versatility",
        description:
          "As at ease on the Croisette as on the hinterland roads and towards the resorts: a single vehicle for all your trips.",
      },
      {
        title: "Discreet statement",
        description:
          "The alternative to the classic luxury of the sedan: an assertive style for clients who like to stand out.",
      },
    ],
  },
  services: {
    heading: "Luxury SUV with chauffeur services",
    services: [
      {
        title: "Event arrivals",
        text: "Evenings, events and receptions: a stylish, noticed arrival at the wheel of a prestige SUV.",
      },
      {
        title: "Mountain & resort transfers",
        text: "Links to the resorts and hinterland roads, where the SUV truly comes into its own.",
      },
      {
        title: "Outings & discoveries",
        text: "Vineyards, hilltop villages and the Nice hinterland: explore the region up high and in comfort.",
      },
      {
        title: "Hourly hire",
        text: "SUV and chauffeur by the hour or day for your travel and your wishes.",
      },
    ],
  },
  locations: {
    heading: "Areas served",
    items: [
      { name: "Cannes" },
      { name: "Monaco" },
      { name: "Nice & airport" },
      { name: "Saint-Tropez" },
      { name: "Antibes — Cap d'Antibes" },
    ],
  },
  generic: {
    heading: "Luxury that asserts itself, in full versatility",
    subheading: "The SUV alternative to the prestige sedan.",
    paragraphs: [
      "The Range Rover combines the elegance of a luxury SUV with the versatility of a vehicle at ease everywhere: city, coast, hinterland and resorts.",
      "High driving position, suspension comfort and assertive presence: a journey that gets noticed, led by a discreet chauffeur.",
      "A single point of contact, transparent packages and the same punctuality standard as across our whole fleet.",
    ],
    bullets: [
      "Luxury SUV for up to 3 passengers",
      "Transparent airport packages",
      "Available 24/7",
    ],
  },
  process: {
    heading: "How to book your SUV with chauffeur",
    steps: [
      "Tell us your route, your stops and your schedule.",
      "Receive a clear package or a per-kilometre estimate.",
      "Assignment of the Range Rover and your dedicated chauffeur.",
      "Stylish, punctual and comfortable pick-up.",
    ],
  },
  faq: [
    {
      q: "Which vehicle is the luxury SUV with chauffeur?",
      a: "A Range Rover, a prestige SUV for up to 3 passengers, ideal for noticed arrivals and versatile city / hinterland trips.",
    },
    {
      q: "What is the difference with the S-Class?",
      a: "The S-Class is the prestige sedan with lounge comfort; the Range Rover offers SUV presence, a high seating position and superior versatility towards the hinterland and resorts.",
    },
    {
      q: "Is it suited to resort transfers?",
      a: "Yes: its versatility makes it the ideal vehicle for links to the resorts and hinterland roads.",
    },
    vehiclePriceFaq("en", "CLASS_R", "a Range Rover with chauffeur"),
    {
      q: "How do I book?",
      a: "Tell us your route and schedule: we quickly confirm the Range Rover and your dedicated chauffeur.",
    },
  ],
  relatedLinks: [
    {
      label: "Mercedes S-Class with chauffeur",
      href: "/en/chauffeur/s-class-with-chauffeur",
    },
    {
      label: "Van with chauffeur (7 seats)",
      href: "/en/chauffeur/van-with-chauffeur",
    },
    { label: "All our chauffeur services", href: "/en/chauffeur" },
    {
      label: "Chauffeur for a wedding in Cannes",
      href: "/en/chauffeur/wedding-car-cannes",
    },
  ],
  cta: {
    title: "Fancy a luxury SUV with chauffeur?",
    text: "Tell us your route — fast quote for your Range Rover with chauffeur.",
    primaryHref: "/contact",
    primaryLabel: "Request a quote",
  },
  seo: {
    ...vehicleSeo(
      "en",
      "CLASS_R",
      "Range Rover with chauffeur, French Riviera",
      "Range Rover with chauffeur",
    ),
    canonical: "/en/chauffeur/suv-with-chauffeur",
  },
};

export default suvWithChauffeur;
